<template>
  <div class="councillor-tab-notes flex justify-center">
    <div
      v-if="hasNotes"
      class="container mb-12 flex flex-col gap-2 items-center justify-start"
    >
      <div class="w-full" v-for="note in relevantNotes" :key="note.id">
        <div class="flex flex-row rounded rounded-lg border shadow">
          <div
            class="rounded-l-lg note-side-bar text-color-white"
            :class="getNoteColor(note)"
          >
            <icon-votes v-if="note.type == types.vote" />
            <icon-news v-else-if="note.type == types.article" />
            <icon-people-group v-else-if="note.type == types.engagment" />
            <icon-parliament v-else-if="note.type == types.profile" />
          </div>
          <div v-if="note.type == types.vote" class="min-w-0 flex-shrink">
            <vote-item
              noPinnedHighlight
              v-if="votes[note.reference]"
              :vote-item="votes[note.reference]"
              :candidate-id="councillor.id"
              style="border:0; border-top-left-radius: 0; border-bottom-left-radius: 0;"
              :show-pin="true"
              :pinned-color="note.color"
              @pinned="onPin(note.type, note.reference)"
              @text-click="goToAffair(votes[note.reference])"
            />
            <icon-loading-spinner
              v-else
              :fade="true"
              color="gray"
              class="absolute-center"
            />
          </div>
          <div v-else-if="note.type == types.article" class="w-full text-left">
            <NewsArticle
              v-if="articles[note.reference]"
              noPinnedHighlight
              :newsCandidate="councillor"
              :article="articles[note.reference]"
              :show-pin="true"
              :pinnedColor="note.color"
              :likeStatus="articleLikes[note.reference]?.color"
              @pinned="onPin(note.type, note.reference)"
              @like="status => onPin(articleLike, note.reference, status)"
            ></NewsArticle>
            <icon-loading-spinner
              v-else
              :fade="true"
              color="gray"
              class="absolute-center"
            />
          </div>
          <div v-else-if="note.type == types.engagment" class="w-full">
            <person-info-slice
              v-if="
                note.reference === 'mandates' && councillorMandates.length > 0
              "
              :color="partyColor"
              :title="$t('views.single.councillor.mandates_subtitle')"
              showPinned
              noPinnedHighlight
              :pinnedColor="note.color"
              @pinned="onPin(note.type, note.reference)"
            >
              <ul class="list-disc text-left">
                <li
                  v-for="(councillorMandate, i) in councillorMandates"
                  :key="i"
                  class="mb-2"
                >
                  {{ councillorMandate }}
                </li>
              </ul>
            </person-info-slice>
            <person-info-slice
              v-else-if="councillor.concerns && note.reference === 'concerns'"
              :color="partyColor"
              showPinned
              :pinnedColor="note.color"
              noPinnedHighlight
              :title="$t('components.people.profile.concerns_title')"
              @pinned="onPin(note.type, note.reference)"
            >
              <concerns :concerns="councillor.concerns" />
            </person-info-slice>
          </div>
          <div v-else-if="note.type == types.profile" class="w-full">
            <person-info-slice
              :color="partyColor"
              :title="councilMembershipsPerReference[note.reference]?.name"
              showPinned
              :pinnedColor="note.color"
              @pinned="onPin(note.type, note.reference)"
            >
              <ul class="list-disc text-left">
                <li
                  v-for="membership in councilMembershipsPerReference[
                    note.reference
                  ]?.memberships"
                  :key="membership.id"
                >
                  {{ membership.committeeName }}
                </li>
              </ul>
            </person-info-slice>
          </div>
        </div>
      </div>
      <delete-pin-modal
        v-if="noteToDelete"
        @delete-pin="deletePin()"
        @dismiss="noteToDelete = null"
      />
    </div>
    <div v-else class="container mb-12">
      <p
        v-html="
          $t('views.single.candidate.no_notes', {
            candidateFirstName: councillor.firstName
          })
        "
      ></p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Pinning } from "@/utils/Pinning";
import IconVotes from "@/components/icons/IconVotes.vue";
import IconParliament from "../icons/IconParliament.vue";
import IconPeopleGroup from "../icons/IconPeopleGroup.vue";
import client from "api-client";
import VoteItem from "../vote/VoteItem.vue";
import IconNews from "../icons/IconNews.vue";
import NewsArticle from "../news/NewsArticle.vue";
import IconLoadingSpinner from "../icons/IconLoadingSpinner.vue";
import PersonInfoSlice from "../people/PersonInfoSlice.vue";
import Concerns from "./profile/Concerns.vue";
import DeletePinModal from "../layouts/DeletePinModal.vue";

export default {
  name: "CouncillorTabNotes",
  components: {
    IconVotes,
    VoteItem,
    IconNews,
    NewsArticle,
    IconLoadingSpinner,
    PersonInfoSlice,
    IconParliament,
    IconPeopleGroup,
    Concerns,
    DeletePinModal
  },
  mixins: [Pinning],
  props: {
    councillor: {
      type: Object,
      required: true
    },
    partyColor: {
      type: String,
      default: "var(--interaction-light)"
    },
    councilMemberships: {
      type: Array,
      default: () => []
    },
    notes: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    types: {
      vote: "councillor_vote_pin",
      article: "councillor_article_pin",
      engagment: "councillor_engagement",
      profile: "councillor_profile"
    },
    articleLike: "councillor_article_like",
    votes: {},
    articles: {},
    noteToDelete: null
  }),
  watch: {
    voteIds: {
      async handler() {
        if (Object.keys(this.votes).length > 0) {
          return;
        }

        const results = await Promise.allSettled(
          this.voteIds.map(id =>
            client.fetchCouncillorVote(
              this.councillor.id,
              id,
              this.contentLanguage
            )
          )
        );

        this.votes = results
          .filter(result => result.status == "fulfilled")
          .map(result => result.value)
          .reduce(
            (acc, vote) => {
              acc[vote.id] = vote;
              return acc;
            },
            { ...this.votes }
          );
      },
      immediate: true
    },
    articleIds: {
      async handler() {
        if (Object.keys(this.articles).length > 0) {
          return;
        }

        const results = await Promise.allSettled(
          this.articleIds.map(client.fetchArticle)
        );

        this.articles = results
          .filter(result => result.status == "fulfilled")
          .map(result => result.value)
          .reduce(
            (acc, article) => {
              acc[article.id] = article;
              return acc;
            },
            { ...this.articles }
          );
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters("user", ["contentLanguage", "user"]),
    councilMembershipsPerReference() {
      return this.councilMemberships.reduce((acc, membership) => {
        acc[`membership/${membership.id}`] = membership;
        return acc;
      }, {});
    },
    councillorMandates() {
      return [
        this.councillor?.mandate,
        this.councillor?.additionalMandate,
        this.councillor?.additionalActivity
      ].filter(Boolean);
    },
    hasNotes() {
      return this.relevantNotes.length > 0;
    },
    relevantNotes() {
      if (!this.notes || !Array.isArray(this.notes)) {
        return [];
      }
      const types = new Set(Object.values(this.types));
      return this.notes.filter(note => types.has(note.type));
    },
    voteIds() {
      return this.relevantNotes
        .filter(note => note.type == this.types.vote)
        .map(note => note.reference);
    },
    articleIds() {
      return this.relevantNotes
        .filter(note => note.type == this.types.article)
        .map(note => note.reference);
    },
    articleLikes() {
      return this.notes
        .filter(note => note.type === this.articleLike)
        .reduce((acc, note) => {
          acc[note.reference] = note;
          return acc;
        }, {});
    }
  },
  methods: {
    getNoteColor(note) {
      return this.getPinClassColor(note.color);
    },
    goToAffair(vote) {
      this.$router.push({
        name: "affair",
        query: {
          voteId: vote.id
        },
        params: {
          id: vote.affair.id
        }
      });
    },
    deletePin() {
      if (this.noteToDelete) {
        this.$emit(
          "pinned",
          this.noteToDelete.type,
          this.noteToDelete.reference
        );
        this.noteToDelete = null;
      }
    },
    onPin(type, reference, color) {
      if (type === this.articleLike) {
        this.$emit("pinned", type, reference, color);
      } else {
        this.noteToDelete = { type, reference };
      }
    }
  }
};
</script>

<style>
.councillor-tab-notes .note-side-bar {
  width: 2.25rem;
  min-width: 2.25rem;
}

.councillor-tab-notes .note-side-bar svg {
  font-size: 1.3125rem;
  margin-top: 0.5rem;
}

.councillor-tab-notes .show-pinned {
  box-shadow: none;
}
</style>
